import { Link, NavLink, useLocation } from "react-router-dom";
import { ShoppingBag, Menu, X } from "lucide-react";
import { useState, useEffect } from "react";
import { useCart } from "../context/CartContext";

const MOUNTAIN_MARK = "/mountain-mark.png";

const navLinks = [
  { to: "/", label: "Home", testId: "nav-home" },
  { to: "/about", label: "About", testId: "nav-about" },
  { to: "/shop", label: "Shop", testId: "nav-shop" },
  { to: "/contact", label: "Contact", testId: "nav-contact" },
];

const Navigation = () => {
  const { totalQty, openCart } = useCart();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  return (
    <header
      data-testid="site-nav"
      className={`fixed top-0 inset-x-0 z-40 transition-colors duration-500 ${
        scrolled ? "bg-black/85 backdrop-blur-md border-b hairline" : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
        <Link to="/" className="flex items-center group" data-testid="nav-logo-link" aria-label="Summit Scents — Home">
          <img
            src={MOUNTAIN_MARK}
            alt="Summit Scents mountain mark"
            className="h-5 md:h-6 w-auto select-none transition-transform duration-500 group-hover:-translate-y-0.5"
            draggable={false}
          />
        </Link>

        <nav className="hidden md:flex items-center gap-10">
          {navLinks.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={l.testId}
              end={l.to === "/"}
              className={({ isActive }) =>
                `text-[11px] uppercase tracking-label transition-colors duration-300 ${
                  isActive ? "gold-text" : "text-[var(--primary-text)]/70 hover:text-[var(--primary-text)]"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-4">
          <button
            data-testid="open-cart-btn"
            onClick={openCart}
            className="relative p-2 text-[var(--primary-text)] hover:text-[var(--gold)] transition-colors duration-300"
            aria-label="Open cart"
          >
            <ShoppingBag size={20} strokeWidth={1.2} />
            {totalQty > 0 && (
              <span
                data-testid="cart-count-badge"
                className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 inline-flex items-center justify-center text-[10px] bg-[var(--gold)] text-black font-semibold"
              >
                {totalQty}
              </span>
            )}
          </button>
          <button
            data-testid="open-mobile-menu-btn"
            onClick={() => setMobileOpen((o) => !o)}
            className="md:hidden p-2 text-[var(--primary-text)]"
            aria-label="Open menu"
          >
            {mobileOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      <div
        className={`md:hidden overflow-hidden bg-black/95 backdrop-blur-md border-b hairline transition-[max-height] duration-500 ${
          mobileOpen ? "max-h-96" : "max-h-0"
        }`}
      >
        <nav className="px-6 py-6 flex flex-col gap-5">
          {navLinks.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`mobile-${l.testId}`}
              end={l.to === "/"}
              className={({ isActive }) =>
                `text-sm uppercase tracking-label ${isActive ? "gold-text" : "text-[var(--primary-text)]/80"}`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>
      </div>
    </header>
  );
};

export default Navigation;
